import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, Inbox, ChevronDown } from "lucide-react";
import { formatINR } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVEL2 = new Set(["introducer", "custodian", "guarantor"]);

const Chip = ({ children, color = "#B87333" }) => (
  <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border" style={{ color, borderColor: `${color}55`, background: `${color}0d` }}>
    {children}
  </span>
);

/**
 * Level-2 sponsor's approval inbox.
 * Dependent-SS orders land here awaiting Approve / Reject.
 * Approve supports an optional commission-percent override and a note.
 * Reject requires a note that flows back to the buyer in an email.
 */
export const SponsorInbox = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [orderId]: { commission, note } }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sponsor/approvals?status=${tab}`);
      setOrders(r.data || []);
    } catch { setOrders([]); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { if (LEVEL2.has(user?.buyer_category)) load(); }, [user, load]);

  if (!user || !LEVEL2.has(user.buyer_category)) return null;

  const draftFor = (id) => drafts[id] || { commission: "", note: "" };
  const setDraft = (id, k, v) => setDrafts((d) => ({ ...d, [id]: { ...draftFor(id), [k]: v } }));

  const approve = async (o) => {
    const d = draftFor(o.id);
    const commission = d.commission ? parseFloat(d.commission) : null;
    if (d.commission && (isNaN(commission) || commission < 0 || commission > 100)) {
      toast.error("Commission override must be 0-100."); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/sponsor/approvals/${o.id}/approve`, {
        commission_override_pct: commission,
        note: d.note || null,
      });
      toast.success("Order approved and forwarded to Vinci Admin.");
      setOpenId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };

  const reject = async (o) => {
    const d = draftFor(o.id);
    if (!(d.note || "").trim()) { toast.error("A rejection note is required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/sponsor/approvals/${o.id}/reject`, { note: d.note.trim() });
      toast.success("Order returned to the buyer with your note.");
      setOpenId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="sponsor-inbox" className="mt-16">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-serif text-2xl md:text-3xl flex items-center gap-3">
          <Inbox size={22} className="text-[#B87333]" />
          Sponsor approval inbox
          {orders.length > 0 && tab === "pending" && (
            <span className="font-grotesk text-xs bg-[#B87333] text-black px-2 py-0.5 rounded-full">{orders.length}</span>
          )}
        </h2>
        <div className="flex items-center gap-1 rounded-full border border-white/12 p-1 bg-black/40 backdrop-blur-md" data-testid="sponsor-inbox-tabs">
          {[
            { k: "pending", l: "Pending" },
            { k: "approved", l: "Approved" },
            { k: "rejected", l: "Rejected" },
            { k: "all", l: "All" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`sponsor-inbox-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 py-1.5 rounded-full font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${
                tab === t.k ? "bg-[#B87333] text-black" : "text-[#B8B8B0] hover:text-[#F5F5F0]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      <p className="font-body text-xs text-[#8A8A82] mb-5 max-w-2xl">
        Every order placed by a Super-Stockist you sponsor lands here first. Approve to forward it to Vinci Admin operations, or reject to return it to the buyer with your reason. Approvals may include an optional commission-percent override for this specific transaction.
      </p>

      {loading && <div className="p-6 font-body text-xs text-[#8A8A82]">Loading…</div>}
      {!loading && orders.length === 0 && (
        <div className="border border-dashed border-white/12 rounded-sm p-8 text-center">
          <p className="font-body text-sm text-[#C9C9C2]">No {tab === "all" ? "" : tab + " "}orders in your inbox.</p>
        </div>
      )}

      <div className="space-y-3">
        {orders.map((o) => {
          const isOpen = openId === o.id;
          const statusColor = o.parent_review_status === "approved" ? "#2FB999" : o.parent_review_status === "rejected" ? "#FF5A5F" : "#B87333";
          return (
            <div
              key={o.id}
              data-testid={`sponsor-order-${o.id}`}
              className="border border-white/10 rounded-sm bg-[#0a0a0a] overflow-hidden"
              style={{ boxShadow: isOpen ? `inset 0 0 0 1px ${statusColor}22` : undefined }}
            >
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : o.id)}
                className="w-full text-left p-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-lg text-[#F5F5F0]">Order {o.id.slice(0, 8)}</span>
                    <Chip color="#B87333">{o.buyer_vendor_code || o.buyer_email}</Chip>
                    <Chip color={statusColor}>{o.parent_review_status}</Chip>
                  </div>
                  <div className="font-body text-xs text-[#8A8A82] mt-1">
                    {o.items?.length || 0} line item{(o.items?.length || 0) === 1 ? "" : "s"}
                    {o.project_name ? ` · ${o.project_name}` : ""}
                    {o.shipping_state ? ` · ${o.shipping_state}` : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif text-xl text-[#F5F5F0] tabular-nums">{formatINR(o.total)}</div>
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82] mt-1">Total incl GST</div>
                </div>
                <ChevronDown size={18} className={`text-[#8A8A82] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-white/[0.08] overflow-hidden"
                  >
                    <div className="p-5">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Line items</div>
                          <ul className="space-y-1 font-body text-xs text-[#C9C9C2]">
                            {(o.items || []).map((it, i) => (
                              <li key={i} className="flex justify-between gap-3">
                                <span className="truncate">{it.quantity} × {it.name}</span>
                                <span className="tabular-nums text-[#8A8A82] shrink-0">{formatINR(it.amount)}</span>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-3 pt-3 border-t border-white/[0.06] font-body text-xs text-[#8A8A82] space-y-1">
                            <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums text-[#C9C9C2]">{formatINR(o.subtotal)}</span></div>
                            {o.discount > 0 && <div className="flex justify-between"><span>Tier discount</span><span className="tabular-nums text-[#2FB999]">−{formatINR(o.discount)}</span></div>}
                            <div className="flex justify-between"><span>GST</span><span className="tabular-nums text-[#C9C9C2]">{formatINR(o.gst)}</span></div>
                            <div className="flex justify-between pt-1 font-grotesk text-[10px] uppercase tracking-[0.2em]"><span>Total</span><span className="tabular-nums text-[#F5F5F0]">{formatINR(o.total)}</span></div>
                          </div>
                        </div>

                        {o.parent_review_status === "pending" ? (
                          <div>
                            <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Your decision</div>
                            <label className="block font-body text-xs text-[#8A8A82] mb-1">Commission override (%) <span className="text-[#6f6f68]">— optional</span></label>
                            <input
                              type="number" step="0.1" min="0" max="100"
                              value={draftFor(o.id).commission}
                              onChange={(e) => setDraft(o.id, "commission", e.target.value)}
                              placeholder="e.g. 2.5"
                              data-testid={`sponsor-commission-${o.id}`}
                              className="w-full bg-transparent border border-white/12 rounded-sm px-3 py-2 font-serif text-base tabular-nums focus:border-white/40 outline-none"
                            />
                            <label className="block font-body text-xs text-[#8A8A82] mt-3 mb-1">Note <span className="text-[#6f6f68]">— required for reject</span></label>
                            <textarea
                              rows={3}
                              value={draftFor(o.id).note}
                              onChange={(e) => setDraft(o.id, "note", e.target.value)}
                              placeholder="Add context for the buyer or the admin desk…"
                              data-testid={`sponsor-note-${o.id}`}
                              className="w-full bg-transparent border border-white/12 rounded-sm px-3 py-2 font-body text-sm focus:border-white/40 outline-none resize-none"
                            />
                            <div className="flex items-center gap-2 mt-4">
                              <button
                                type="button" disabled={busy}
                                data-testid={`sponsor-approve-${o.id}`}
                                onClick={() => approve(o)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2FB999] text-black font-grotesk text-[10px] uppercase tracking-[0.18em] disabled:opacity-60"
                              >
                                <CheckCircle2 size={13} /> Approve & forward
                              </button>
                              <button
                                type="button" disabled={busy}
                                data-testid={`sponsor-reject-${o.id}`}
                                onClick={() => reject(o)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#FF5A5F]/60 text-[#FF5A5F] font-grotesk text-[10px] uppercase tracking-[0.18em] disabled:opacity-60"
                              >
                                <XCircle size={13} /> Reject with note
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Decision on file</div>
                            <div className="font-body text-xs text-[#C9C9C2] space-y-1.5">
                              <div>Status: <b style={{ color: statusColor }}>{o.parent_review_status}</b></div>
                              {o.parent_review_commission_pct != null && <div>Commission override: <b>{o.parent_review_commission_pct}%</b></div>}
                              {o.parent_review_note && (
                                <div className="pt-2 border-t border-white/[0.06]"><span className="text-[#8A8A82]">Note:</span> {o.parent_review_note}</div>
                              )}
                              {o.parent_reviewed_at && <div className="text-[#8A8A82] text-[10px]">Recorded {new Date(o.parent_reviewed_at).toLocaleString("en-IN")}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};
